import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import AuthModal from "@/components/auth/AuthModal";

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  branches: string[];
  displayOrder?: number;
}

interface Branch {
  id: string;
  name: string;
  address: string;
  contact: string;
  email: string;
  social: string;
}

interface CourseSectionProps {
  courses?: Course[];
  isAdmin?: boolean;
  onEditCourse?: (course: Course) => void;
}

const CourseSection = ({
  courses: propCourses,
  isAdmin = false,
  onEditCourse = () => {},
}: CourseSectionProps) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>("");
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // Fetch courses and branches from Firestore
    const fetchData = async () => {
      const [coursesSnap, branchesSnap] = await Promise.all([
        getDocs(collection(db, "courses")),
        getDocs(collection(db, "branches"))
      ]);
      
      const fetchedCourses = coursesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Course));
      const fetchedBranches = branchesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as Branch));
      
      // Sort courses by displayOrder, putting courses without displayOrder at the end
      const sortedCourses = fetchedCourses.sort((a, b) => {
        const aOrder = (a as any).displayOrder ?? 999999;
        const bOrder = (b as any).displayOrder ?? 999999;
        return aOrder - bOrder;
      });
      
      setCourses(sortedCourses);
      setBranches(fetchedBranches);
      
      // Set the first branch as the default active filter
      if (fetchedBranches.length > 0) {
        setActiveFilter(fetchedBranches[0].id);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (activeFilter === "" || activeFilter === "all") {
      setFilteredCourses(courses);
    } else {
      const branchCourses = courses.filter((course) => course.branches && course.branches.includes(activeFilter));
      
      // Sort by branch-specific display order
      const branchKey = `displayOrder_${activeFilter}`;
      const sortedBranchCourses = branchCourses.sort((a, b) => {
        const aOrder = (a as any)[branchKey] ?? (a as any).displayOrder ?? 999999;
        const bOrder = (b as any)[branchKey] ?? (b as any).displayOrder ?? 999999;
        return aOrder - bOrder;
      });
      
      setFilteredCourses(sortedBranchCourses);
    }
  }, [courses, activeFilter]);

  // Helper function to get branch name
  const getBranchName = (branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };

  return (
    <div>
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight mb-2">
              Our Driving Courses
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose from our wide range of professional driving courses designed
              to help you become a confident and skilled driver.
            </p>
          </div>

          {/* Branch filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {branches.map((branch) => (
              <Button
                key={branch.id}
                variant={activeFilter === branch.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(branch.id)}
              >
                {branch.name}
              </Button>
            ))}
          </div>

          {/* Courses grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <Card
                key={course.id}
                className="flex flex-col h-full overflow-hidden transition-all hover:shadow-lg"
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle>{course.title}</CardTitle>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {course.branches && course.branches.map((branchId) => (
                      <Badge key={branchId} variant="secondary" className="text-xs">
                        {getBranchName(branchId)}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-sm text-muted-foreground mb-4">
                    {course.description}
                  </p>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{course.duration}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="default" onClick={() => setShowAuthModal(true)}>
                    Enroll Now
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={() => onEditCourse(course)}
                    >
                      Edit
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No courses found for this branch.
              </p>
            </div>
          )}
        </div>
      </section>

      {showAuthModal && (
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} defaultTab="signup" />
      )}
    </div>
  );
};

export default CourseSection;
